import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CreditCard } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { UpdateCardDialog } from '@/components/manager/subscription/UpdateCardDialog'
import { logout } from '@/lib/auth'

/**
 * Écran bloquant affiché à la place du dashboard pour un manager en essai qui
 * a démarré son trial mais n'a jamais enregistré de carte (sub `trialing` sans
 * moyen de paiement). Ferme le bypass : l'user ne peut rien faire tant qu'il
 * n'a pas ajouté sa carte.
 *
 * On NE redirige PAS vers /checkout (qui recréerait un abonnement à chaque
 * passage). On ouvre `UpdateCardDialog` (SetupIntent) qui rattache la carte au
 * Customer/essai EXISTANT — aucun nouvel abonnement. Sur succès, on invalide la
 * query `payment-method` : l'OnboardingGuard se re-rend et laisse passer.
 */
export function TrialPaymentGate() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md text-center space-y-6">
        <div className="mx-auto h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
          <CreditCard className="h-6 w-6 text-blue-500" />
        </div>

        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">
            {t('trialGate.title', 'Ajoutez votre carte pour continuer')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'trialGate.description',
              "Aucun montant ne sera prélevé pendant votre essai. Votre carte est requise pour activer votre tableau de bord.",
            )}
          </p>
        </div>

        <Button
          className="w-full bg-blue-600 hover:bg-blue-700"
          onClick={() => setOpen(true)}
        >
          {t('trialGate.cta', 'Ajouter ma carte')}
        </Button>

        <button
          type="button"
          onClick={() => logout()}
          className="text-xs text-muted-foreground hover:underline"
        >
          {t('common.logout', 'Se déconnecter')}
        </button>
      </div>

      <UpdateCardDialog
        open={open}
        onClose={() => setOpen(false)}
        onSuccess={async () => {
          await queryClient.invalidateQueries({ queryKey: ['payment-method'] })
        }}
        description={t(
          'trialGate.dialogDescription',
          "Aucun montant ne sera prélevé pendant l'essai. Votre carte sera utilisée à la fin de la période d'essai.",
        )}
      />
    </div>
  )
}
